import { ContentWrapper } from '@eti/components';
import { css } from '@eti/styles';
import { breakpoints, Card, Column, Columns, Heading, Stack, Text } from '@etg/wings';
import { useProperty, useTranslation } from '@eti/providers';
import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { EventSegmentType } from '@eti/schema-types';
import { useMediaQuery } from 'usehooks-ts';
import Payment from '../overview/payment/Payment';
import Documents from '../overview/documents/Documents';
import { screenReadersOnlyStyles } from '../../../../utils/styleUtils';
import MedicalCertificate from '../common/MedicalCertificate';
import TitledCard from '../../../../common/components/TitledCard';
import { TRAVEL_DOCS_INFO } from '../../containers/queries';
import FareRulesDrawerButton from '../common/FareRulesDrawerButton';
import { shouldShowFareRules } from '../../../receipt/utils/utils';
import { useOrderDetails } from '../../containers/OrderDetailsContext';
import TripEventsCards from '../common/TripEventsCards';
import useHashScroll from '../../../post-booking/ancillary-store/custom-hooks/hashScroll';
import { useRoutedTabs } from '../../../../widgets/tabs/components/RoutedTabs';
import FaqSection from '../common/FaqSection';
import TravelDocumentsAndVisa from './TravelDocumentsAndVisa';
import BeforeYourTrip from './BeforeYourTrip';

const contentWrapperStyles = css`
  margin-bottom: 16px;
  margin-top: 16px;

  @media (min-width: ${breakpoints._768}) {
    margin-bottom: 40px;
    margin-top: 24px;
  }
`;

const OrderDetailsTravelInformation = () => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { bounds, eventTypes, faqSectionPerSlug, fareRules, fareRulesExtraInfo } =
    useOrderDetails();
  const eventTypesWithoutStop = eventTypes.filter((type) => type !== EventSegmentType.Stop);
  const { tabHeading1Ref } = useRoutedTabs();
  const isMobileScreen = useMediaQuery(`(max-width: ${breakpoints._768})`);
  const {
    loading,
    data: travelDocData,
    error,
    called,
  } = useQuery(TRAVEL_DOCS_INFO, {
    fetchPolicy: 'no-cache',
  });

  useHashScroll(true, called && !loading);

  const hasTechnicalStops = useMemo(
    () =>
      bounds.some((bound) =>
        bound.segments.some((segment) => {
          if (segment.__typename === 'TripSegment') {
            return segment.numberOfTechnicalStops > 0;
          }
          return false;
        }),
      ),
    [bounds],
  );

  const faqSectionSlug = p('OrderDetails.TravelInformation.Faq.Slug');
  const isMedCertVisible = p('OrderDetails.MedicalCertificate.Enabled');
  const areFareRulesVisible = shouldShowFareRules({ p, t, fareRules, texts: fareRulesExtraInfo });

  return (
    <ContentWrapper className={contentWrapperStyles} data-testid="order-details-travel-information">
      <h1 className={screenReadersOnlyStyles} ref={tabHeading1Ref} tabIndex={-1}>
        {t('OrderDetails.Tab.TravelInformation.Description')}
      </h1>
      <Columns
        collapseBelow={breakpoints._768}
        spacing={{ [breakpoints._0]: 8, [breakpoints._768]: 24 }}
      >
        <Column width={{ [breakpoints._0]: '12/12', [breakpoints._768]: '7/12' }}>
          <Stack spacing={{ [breakpoints._0]: 8, [breakpoints._768]: 16 }}>
            {travelDocData?.travelDocsInfo?.carrierInfo.map((carrierInfoItem) => (
              <Card
                data-testid={`carrier-info-${carrierInfoItem.carrier.code}`}
                key={carrierInfoItem.carrier.code}
              >
                <Stack>
                  <Heading level={2}>{carrierInfoItem.title}</Heading>
                  <Text asHtml>{carrierInfoItem.description}</Text>
                </Stack>
              </Card>
            ))}
            {(eventTypesWithoutStop.length > 0 || hasTechnicalStops) && (
              <TitledCard
                data-testid="important-information"
                title={t('OrderDetails.TravelInformation.TripInformation.Title')}
              >
                <Stack>
                  <TripEventsCards
                    eventTypes={
                      hasTechnicalStops
                        ? [...eventTypesWithoutStop, 'TECHNICAL_STOP']
                        : eventTypesWithoutStop
                    }
                    headingLevel={3}
                  />
                </Stack>
              </TitledCard>
            )}
            <BeforeYourTrip />
            {!isMobileScreen && faqSectionPerSlug?.[faqSectionSlug] && (
              <FaqSection section={faqSectionPerSlug[faqSectionSlug]} shouldRedirectToContactUs />
            )}
          </Stack>
        </Column>
        <Column width={{ [breakpoints._0]: '12/12', [breakpoints._768]: '5/12' }}>
          <Stack spacing={{ [breakpoints._0]: 8, [breakpoints._768]: 16 }}>
            <TravelDocumentsAndVisa
              hasError={Boolean(error)}
              isLoading={loading}
              travelDocsInfo={travelDocData?.travelDocsInfo}
            />
            <Payment />
            <Documents />
            {(areFareRulesVisible || isMedCertVisible) && (
              <TitledCard
                data-testid="additional-information"
                title={t('ManageTrip.Cancel.AdditionalInformation.Title')}
              >
                <Stack>
                  {areFareRulesVisible && (
                    <FareRulesDrawerButton
                      snowplow={{
                        category: 'Travel Information',
                        label: 'FARE_RULES',
                      }}
                    />
                  )}
                  {isMedCertVisible && (
                    <MedicalCertificate
                      snowplow={{
                        category: 'Travel Information',
                        label: 'MEDICAL_CERTIFICATE',
                      }}
                    />
                  )}
                </Stack>
              </TitledCard>
            )}
            {isMobileScreen && faqSectionPerSlug?.[faqSectionSlug] && (
              <FaqSection section={faqSectionPerSlug[faqSectionSlug]} shouldRedirectToContactUs />
            )}
          </Stack>
        </Column>
      </Columns>
    </ContentWrapper>
  );
};

export default OrderDetailsTravelInformation;
